import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneRole, UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { UserRole } from '../../../../types';
import { useNavigate } from 'react-router-dom';
import { Messages } from '../../../utils/message-constants';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface CloneRoleDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRole: UserRole;
  changeLogRoles: string[];
  roleNames: string[];
}

export default function CloneRoleDialog({
  open,
  handleClose,
  selectedRole,
  changeLogRoles,
  roleNames,
}: CloneRoleDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleNameError, setNewRoleNameError] = useState(false);
  const [newRoleNameExistsError, setNewRoleNameExistsError] = useState(false);
  const queryClient = useQueryClient();
  const [newRoleDescription, setNewRoleDescription] = useState(
    selectedRole.desc
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      cloneRole(
        newRoleName.trim(),
        selectedRole.roleName,
        newRoleDescription,
        changeLogMessage
      ),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Cloned('Role'),
      });

      queryClient.setQueryData([UserKeys.Roles], (oldData: UserRole[]) => {
        if (!oldData) return;
        return [...oldData, data];
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRoles],
      });
      navigate(`./${encodeURIComponent(data.roleName)}`);
      setNewRoleName('');
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Cloning('the role'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.role_manager.clone_role, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
        roleType: selectedRole.roleType,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (newRoleName.trim().length === 0) {
      setNewRoleNameError(true);
    } else if (roleNamesLowerCase.includes(newRoleName.toLowerCase().trim())) {
      setNewRoleNameExistsError(true);
    } else {
      setNewRoleNameExistsError(false);
      setNewRoleNameError(false);
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          AdministrationRoles.CreateRole.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  const roleNamesLowerCase = roleNames.map((name) => name.toLowerCase());

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setNewRoleName('');
        setNewRoleNameError(false);
        setNewRoleNameExistsError(false);
        handleClose();
      }}
      title="Clone Role"
      dialogContent={
        <Stack gap={1}>
          <Typography>
            You are about to clone the selected role named:{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {selectedRole.roleName}
            </Box>
          </Typography>
          <Typography>
            Please provide a role name and description for the newly cloned
            role.
          </Typography>
          <TextField
            autoFocus
            required
            value={newRoleName}
            onChange={(event) => setNewRoleName(event.target.value)}
            label="New Role Name"
            fullWidth
            autoComplete="off"
            sx={{ mt: 1 }}
            error={newRoleNameError || newRoleNameExistsError}
            helperText={
              newRoleNameError
                ? 'Please provide a new role name.'
                : newRoleNameExistsError
                ? 'Role name is currently in use. Please provide a different name.'
                : ' '
            }
          />
          <TextField
            value={newRoleDescription}
            onChange={(event) => setNewRoleDescription(event.target.value)}
            label="New Role Description"
            fullWidth
            multiline
            rows={3}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Clone Role',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending || newRoleName.trim().length === 0,
      }}
    />
  );
}
